/**
 * The reverse verification panel: the token the user sends to one of our chat
 * accounts, the deep links that carry it, and the poll that waits for the
 * inbound message to arrive.
 *
 * The plaintext token exists only in the create response. A status read reports
 * that a challenge is outstanding and never reproduces the token, so the panel
 * keeps its own copy for a reload. A create call inside the session TTL joins
 * the live session and returns the same token, which is the recovery path when
 * that copy is gone.
 *
 * The countdown runs off `expires_in` rather than `expires_at`: the timestamp
 * is a server-local datetime with no zone, which browsers parse inconsistently.
 */

export const REVERSE_POLL_INTERVAL_MS = 5000;
/** Ticks skipped after the nth consecutive failed status read, capped. */
export const REVERSE_POLL_MAX_SKIPS = 5;
export const REVERSE_COUNTDOWN_INTERVAL_MS = 1000;
export const REVERSE_STORAGE_KEY = 'phone-verification-reverse-challenge';

export const REVERSE_WHATSAPP = 'whatsapp';
export const REVERSE_TELEGRAM = 'telegram';

// Written out in full because Tailwind's JIT scans this file for literals, so
// a class assembled from fragments would never be generated. The leading
// channel takes the same primary treatment as the page's own send button.
const LINK_PRIMARY_CLASSES = [
    'bg-sky-500', 'text-white', 'shadow-md',
    'hover:bg-sky-600', 'active:bg-sky-700',
    'dark:bg-sky-600', 'dark:hover:bg-sky-500', 'dark:active:bg-sky-700',
];
const LINK_SECONDARY_CLASSES = [
    'border-2', 'border-sky-300', 'bg-white', 'text-sky-700',
    'hover:bg-sky-100', 'active:bg-sky-200',
    'dark:border-sky-700', 'dark:bg-gray-800', 'dark:text-sky-200',
    'dark:hover:bg-sky-900/50', 'dark:active:bg-sky-900',
];

/** The offer order for a challenge, leading channel first. */
export function reverseChannelOrder(leads) {
    return leads === REVERSE_TELEGRAM
        ? [REVERSE_TELEGRAM, REVERSE_WHATSAPP]
        : [REVERSE_WHATSAPP, REVERSE_TELEGRAM];
}

/**
 * The challenge a create response carries, or null when it carries none.
 *
 * A challenge with no link is dropped: the token alone gives the user nowhere
 * to send it.
 */
export function reverseChallengeFrom(data) {
    const reverse = data && data.reverse;
    if (!reverse || typeof reverse.token !== 'string' || reverse.token === '') return null;

    const urls = {};
    if (typeof reverse.whatsapp_url === 'string' && reverse.whatsapp_url !== '') {
        urls[REVERSE_WHATSAPP] = reverse.whatsapp_url;
    }
    if (typeof reverse.telegram_url === 'string' && reverse.telegram_url !== '') {
        urls[REVERSE_TELEGRAM] = reverse.telegram_url;
    }
    if (!urls[REVERSE_WHATSAPP] && !urls[REVERSE_TELEGRAM]) return null;

    const expiresIn = Number(reverse.expires_in);

    return {
        clicked: false,
        leads: reverse.leads === REVERSE_TELEGRAM ? REVERSE_TELEGRAM : REVERSE_WHATSAPP,
        remainingSeconds: isFinite(expiresIn) && expiresIn > 0 ? Math.floor(expiresIn) : 0,
        token: String(reverse.token),
        urls,
    };
}

/** Whether $iso is a corridor that gets no paid send. */
export function isReverseOnlyCountry(iso, reverseOnly) {
    if (!iso || !Array.isArray(reverseOnly)) return false;

    return reverseOnly.indexOf(String(iso).toUpperCase()) !== -1;
}

/**
 * Whether the reverse card leads the step for $iso.
 *
 * $paidLeads is the exclusion list the page renders from
 * `PhoneChannelRoute::PAID_LEADS`. An absent or empty list answers false, so a
 * render that loses the attribute keeps the paid lead rather than flipping
 * every corridor to the reverse one.
 */
export function isReverseLeadCountry(iso, paidLeads) {
    if (!iso || !Array.isArray(paidLeads) || paidLeads.length === 0) return false;

    return paidLeads.indexOf(String(iso).toUpperCase()) === -1;
}

/** Minutes and seconds, which reads the same in every locale. */
export function formatCountdown(seconds) {
    const whole = Math.max(0, Math.floor(Number(seconds) || 0));
    const rest = whole % 60;

    return Math.floor(whole / 60) + ':' + (rest < 10 ? '0' : '') + rest;
}

/**
 * Put text on the clipboard.
 *
 * The async API needs a secure context and a permission the page may not hold,
 * so a refusal falls back to the selection copy every browser still supports.
 */
export function copyTextToClipboard(text) {
    const value = String(text);

    const fallback = () => {
        try {
            const field = document.createElement('textarea');
            field.value = value;
            field.setAttribute('readonly', 'readonly');
            field.className = 'pointer-events-none fixed left-0 top-0 h-px w-px opacity-0';
            document.body.appendChild(field);
            field.select();
            const copied = document.execCommand('copy');
            document.body.removeChild(field);

            return copied ? Promise.resolve() : Promise.reject(new Error('copy_refused'));
        } catch (e) {
            return Promise.reject(e);
        }
    };

    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        return navigator.clipboard.writeText(value).catch(fallback);
    }

    return fallback();
}

/**
 * The panel controller.
 *
 * Every collaborator is injected so the panel can be driven without a browser.
 *
 * @param {Object} options
 * @param {Object} options.elements panel nodes, by role
 * @param {Object} options.storage read()/write(value)/remove() over one owned key
 * @param {Function} options.createSession (countryIso) => Promise<Object> create response body
 * @param {Function} options.requestStatus () => Promise<{response: Object, data: Object}>
 * @param {Function} options.selectedCountry () => ISO-3166-1 alpha-2 of the chosen country
 * @param {Function} options.isReverseOnly (iso) => boolean
 * @param {Function} options.isReverseLead (iso) => boolean
 * @param {Function} options.onPaidSendOffered (offered) => void
 * @param {Function} options.onReverseLeads (leads) => void the page reorders the step
 * @param {Function} [options.sendStalled] () => boolean the page holds a send the card leads
 * @param {Function} options.onVerified the page's own completion path
 * @param {Function} options.onWaived the page's own provider-waiver path
 * @param {Function} options.createQrSvg (text) => SVG markup for a deep link
 */
export function createReverseVerification(options) {
    const elements = options.elements || {};
    const timers = options.timers;
    const trans = options.trans;
    const now = options.now;
    const storage = options.storage;
    const visibility = options.visibility;
    const copyToClipboard = options.copyToClipboard || copyTextToClipboard;
    const initIcons = options.initIcons || function () {};
    const createQrSvg = options.createQrSvg;

    const state = {
        challenge: null,
        copiedTimer: null,
        countdownTimer: null,
        creating: false,
        expiresAtMs: 0,
        phase: 'idle',
        pollFailures: 0,
        pollSkips: 0,
        pollTimer: null,
        reverseLeads: false,
        reverseOnly: false,
        step: 'phone',
        visible: false,
    };

    function setHidden(element, hidden) {
        if (element) element.classList.toggle('hidden', hidden);
    }

    function setText(element, text) {
        if (element && typeof text === 'string' && text !== '') element.textContent = text;
    }

    function remainingSeconds() {
        if (!state.expiresAtMs) return 0;

        return Math.max(0, Math.ceil((state.expiresAtMs - now()) / 1000));
    }

    function persist() {
        if (!storage || !state.challenge) return;

        storage.write(JSON.stringify({
            clicked: state.challenge.clicked === true,
            expiresAtMs: state.expiresAtMs,
            leads: state.challenge.leads,
            token: state.challenge.token,
            urls: state.challenge.urls,
        }));
    }

    function forget() {
        if (storage) storage.remove();
    }

    function restore() {
        if (!storage) return null;

        let stored = null;
        try {
            stored = JSON.parse(storage.read() || 'null');
        } catch (e) {
            stored = null;
        }

        const expiresAtMs = stored ? Number(stored.expiresAtMs) : NaN;
        if (
            !stored
            || typeof stored.token !== 'string'
            || stored.token === ''
            || !isFinite(expiresAtMs)
            || expiresAtMs <= now()
        ) {
            forget();
            return null;
        }

        const urls = stored.urls && typeof stored.urls === 'object' ? stored.urls : {};
        if (!urls[REVERSE_WHATSAPP] && !urls[REVERSE_TELEGRAM]) {
            forget();
            return null;
        }

        return {
            challenge: {
                clicked: stored.clicked === true,
                leads: stored.leads === REVERSE_TELEGRAM ? REVERSE_TELEGRAM : REVERSE_WHATSAPP,
                token: String(stored.token),
                urls,
            },
            expiresAtMs,
        };
    }

    function stopCountdown() {
        if (state.countdownTimer !== null) {
            timers.clearInterval(state.countdownTimer);
            state.countdownTimer = null;
        }
    }

    function stopPolling() {
        if (state.pollTimer !== null) {
            timers.clearInterval(state.pollTimer);
            state.pollTimer = null;
        }
    }

    function renderCountdown() {
        const seconds = remainingSeconds();
        setText(elements.countdown, trans('reverse_expires_in', { time: formatCountdown(seconds) }));

        if (seconds <= 0) expire();
    }

    function startCountdown() {
        stopCountdown();
        renderCountdown();
        if (state.phase !== 'challenge') return;

        state.countdownTimer = timers.setInterval(renderCountdown, REVERSE_COUNTDOWN_INTERVAL_MS);
    }

    // A failed read is not an answer, so the panel asks again. Consecutive
    // failures skip ticks instead, so an outage is not polled at full rate for
    // the whole ten minutes. One answer clears it.
    function readFailed() {
        state.pollFailures += 1;
        state.pollSkips = Math.min(state.pollFailures - 1, REVERSE_POLL_MAX_SKIPS);
    }

    async function poll() {
        if (state.pollSkips > 0) {
            state.pollSkips -= 1;
            return;
        }

        // The status read and the page's confirm take the same per-user
        // challenge lock, and the loser of that race takes a 503. A correct
        // code must not fail because this panel was reading.
        if (paidBusy()) return;

        let result = null;
        try {
            result = await options.requestStatus();
        } catch (e) {
            readFailed();
            return;
        }

        if (state.phase !== 'challenge') return;

        const response = result && result.response;
        const data = result && result.data;
        if (!response || !response.ok || !data) {
            readFailed();
            return;
        }

        state.pollFailures = 0;
        state.pollSkips = 0;

        if (data.required === false) {
            stop();
            forget();
            if (data.status === 'waived') options.onWaived();
            else options.onVerified();
            return;
        }

        // The server dropped the challenge before the local clock reached its
        // expiry, so the panel follows the server rather than its own timer.
        if (!data.reverse || data.reverse.has_reverse !== true) expire();
    }

    function startPolling(immediate) {
        if (state.phase !== 'challenge' || !state.challenge) return;
        if (visibility.isHidden()) return;
        if (state.pollTimer !== null) return;

        state.pollTimer = timers.setInterval(poll, REVERSE_POLL_INTERVAL_MS);
        if (immediate) poll();
    }

    function renderLinks(challenge) {
        reverseChannelOrder(challenge.leads).forEach(function (channel, index) {
            const link = elements.links && elements.links[channel];
            if (!link) return;

            const url = challenge.urls[channel];
            if (!url) {
                setHidden(link, true);
                link.classList.remove('flex');
                return;
            }

            link.setAttribute('href', url);
            LINK_PRIMARY_CLASSES.concat(LINK_SECONDARY_CLASSES).forEach(function (name) {
                link.classList.remove(name);
            });
            (index === 0 ? LINK_PRIMARY_CLASSES : LINK_SECONDARY_CLASSES).forEach(function (name) {
                link.classList.add(name);
            });
            link.classList.add('flex');
            setHidden(link, false);
            // The leading channel is the first action, so the DOM order follows
            // the offer order rather than the order the template declares.
            if (elements.linkList) elements.linkList.appendChild(link);
        });
    }

    /** Drop every code, so an ended challenge leaves nothing scannable. */
    function clearQrCodes() {
        [REVERSE_WHATSAPP, REVERSE_TELEGRAM].forEach(function (channel) {
            const host = elements.qrHosts && elements.qrHosts[channel];
            const figure = elements.qrFigures && elements.qrFigures[channel];

            if (host) host.innerHTML = '';
            if (!figure) return;

            setHidden(figure, true);
            figure.classList.remove('flex');
        });
    }

    /**
     * One code per offered channel, in the offer order.
     *
     * A code carries the same deep link as the button beside it, so a desktop
     * user opens the chat app on a phone with the token already in the message.
     */
    function renderQrCodes(challenge) {
        clearQrCodes();
        if (typeof createQrSvg !== 'function') return;

        reverseChannelOrder(challenge.leads).forEach(function (channel) {
            const host = elements.qrHosts && elements.qrHosts[channel];
            const figure = elements.qrFigures && elements.qrFigures[channel];
            const url = challenge.urls[channel];
            if (!host || !figure || !url) return;

            let svg = '';
            try {
                svg = createQrSvg(url);
            } catch (e) {
                svg = '';
            }

            // The button is the route on every width, so a link the encoder
            // cannot hold costs the user nothing.
            if (!svg) return;

            host.innerHTML = svg;
            setHidden(figure, false);
            figure.classList.add('flex');
            // The DOM order follows the offer order, as the links do.
            if (elements.qrList) elements.qrList.appendChild(figure);
        });
    }

    // The line explains a send button that is not there, so it belongs to the
    // pre-challenge state and leaves with it. Reverse-only is what puts it on
    // screen, so a restore re-reads the corridor: showing it unconditionally
    // would put it back in a paid-lead corridor.
    function renderNoSms() {
        const offerStands = state.phase !== 'challenge' && state.phase !== 'expired';
        setHidden(elements.noSms, !state.reverseOnly || !offerStands);
    }

    function showChallenge(challenge, expiresAtMs) {
        state.challenge = challenge;
        state.expiresAtMs = expiresAtMs;
        state.phase = 'challenge';

        setText(elements.token, challenge.token);
        renderLinks(challenge);
        renderQrCodes(challenge);
        setHidden(elements.offer, true);
        renderNoSms();
        setHidden(elements.timeout, true);
        setHidden(elements.challenge, false);
        // The user can copy the token and message us from another device, so
        // the panel waits from the moment the token is readable, not from a tap.
        setHidden(elements.waiting, false);
        state.pollFailures = 0;
        state.pollSkips = 0;
        persist();
        startCountdown();
        startPolling(challenge.clicked === true);
        initIcons(elements.panel);
    }

    function showOffer() {
        state.phase = 'offer';
        setHidden(elements.challenge, true);
        setHidden(elements.timeout, true);
        setHidden(elements.offer, false);
        renderNoSms();
    }

    function showTimeout(messageKey) {
        state.phase = 'expired';
        setText(elements.timeoutText, trans(messageKey));
        setHidden(elements.challenge, true);
        setHidden(elements.offer, true);
        renderNoSms();
        setHidden(elements.timeout, false);
    }

    function expire() {
        stopCountdown();
        stopPolling();
        state.challenge = null;
        state.expiresAtMs = 0;
        forget();
        clearQrCodes();
        showTimeout('reverse_timeout');
        // The move waits for the challenge to end, so the corridor it held back
        // is applied here.
        renderCorridor();
    }

    function setBusy(busy) {
        setHidden(elements.spinner, !busy);
        if (elements.showButton) elements.showButton.disabled = busy;
        if (elements.startAgainButton) elements.startAgainButton.disabled = busy;
    }

    async function create() {
        // A second tap adds nothing while the first create is in flight.
        if (state.creating) return;

        // The page and this panel take the same per-user challenge lock, and the
        // loser takes a 503. The page can hold that lock for a whole session.
        if (paidBusy()) {
            showTimeout('unavailable');
            return;
        }

        state.creating = true;
        setBusy(true);

        try {
            const data = await options.createSession(options.selectedCountry());
            const challenge = reverseChallengeFrom(data);
            if (!challenge) {
                // A session with no reverse block leaves nothing to show, and a
                // corridor with no paid send has nothing else to offer either.
                showTimeout('unavailable');
                return;
            }

            showChallenge(challenge, now() + (challenge.remainingSeconds * 1000));
        } catch (e) {
            // A refusal the page can name belongs on the page, because the
            // panel's own retry button cannot clear a cap or a cooldown.
            if (options.onCreateRefused && options.onCreateRefused(e)) showOffer();
            else showTimeout('unavailable');
        } finally {
            state.creating = false;
            setBusy(false);
        }
    }

    /**
     * Forget the held challenge.
     *
     * Starting a paid session writes `reverse_token_hash = NULL` on the row, so
     * a token minted against the session it replaces can never verify. The
     * caller owns that transition. {@see PhoneVerificationGateway::startChallenge}
     */
    function dropChallenge() {
        stopCountdown();
        stopPolling();
        state.challenge = null;
        state.expiresAtMs = 0;
        state.phase = 'idle';
        forget();
        clearQrCodes();
        if (!state.visible) return;

        showOffer();
        renderCorridor();
    }

    /**
     * Take back a refusal that holds no challenge, so the next show offers the
     * tap again. A refusal the page's held send caused ends with the hold.
     */
    function clearRefusal() {
        if (state.phase === 'expired' && !state.challenge) state.phase = 'idle';
    }

    /** Whether the page holds the per-user challenge lock this panel needs. */
    function paidBusy() {
        return Boolean(options.paidSendBusy && options.paidSendBusy());
    }

    function linkOpened() {
        if (!state.challenge) return;

        state.challenge.clicked = true;
        persist();
        startPolling(false);
    }

    async function copyToken() {
        if (!state.challenge) return;

        try {
            await copyToClipboard(state.challenge.token);
        } catch (e) {
            return;
        }

        setText(elements.copyLabel, trans('reverse_copied'));
        if (state.copiedTimer !== null) timers.clearTimeout(state.copiedTimer);
        state.copiedTimer = timers.setTimeout(function () {
            state.copiedTimer = null;
            setText(elements.copyLabel, trans('reverse_copy'));
        }, 2000);
    }

    /** Hide the panel and pause its timers. The held challenge survives. */
    function hide() {
        stopCountdown();
        stopPolling();
        state.visible = false;
        state.reverseLeads = false;
        setHidden(elements.panel, true);
        // The heading above the phone form names this card, so it goes with it.
        // refreshCorridor() answers nothing while the panel is hidden.
        options.onReverseLeads(false, false, state.step);
    }

    /** Hide the panel and drop the challenge with it. */
    function stop() {
        hide();
        clearQrCodes();
        state.phase = 'idle';
        state.challenge = null;
        state.expiresAtMs = 0;
    }

    /**
     * Re-read the corridor of the chosen country.
     *
     * A corridor with no paid send withdraws the page's send button, which
     * leaves this panel as the only route there.
     */
    function renderCorridor() {
        if (!state.visible) return;

        const country = options.selectedCountry();
        state.reverseOnly = options.isReverseOnly(country);
        // A stalled paid send may never finish, so the card leads the phone
        // step while the page holds it.
        const stalled = Boolean(options.sendStalled && options.sendStalled());
        // The code step already has a paid code outstanding, so the panel is the
        // alternate there whatever the corridor would lead with.
        state.reverseLeads = state.step === 'phone'
            && (state.reverseOnly || stalled || options.isReverseLead(country));

        const paidOffered = !state.reverseOnly;
        options.onPaidSendOffered(paidOffered);
        renderNoSms();
        // Two questions, not one. A reverse-only corridor leads with the card and
        // has no paid send underneath it to name.
        // The page answers with the position the card actually took. A live
        // challenge can hold the move back, and a heading that names a lead the
        // card does not hold leaves no heading naming the primary action.
        // A stalled send is held, so no heading names it under the card.
        const placed = options.onReverseLeads(
            state.reverseLeads,
            paidOffered && !stalled,
            state.step,
        ) === true;
        setText(
            elements.heading,
            trans(placed ? 'reverse_heading' : 'reverse_alternative_heading'),
        );
    }

    /**
     * Render the corridor and put the panel back in its offer state.
     *
     * A challenge on display keeps it. {@see setStep} follows the page between
     * steps and must leave a held challenge alone, so it does not call this.
     */
    function refreshCorridor() {
        renderCorridor();
        if (!state.visible) return;
        if (state.phase === 'challenge' || state.phase === 'expired') return;
        // No corridor mints without a tap. A visit after the session expires
        // spends one of the day's three.
        showOffer();
    }

    function show(step) {
        state.step = step;
        state.visible = true;
        setHidden(elements.panel, false);

        if (state.phase === 'idle') {
            const restored = restore();
            if (restored) {
                showChallenge(restored.challenge, restored.expiresAtMs);
            }
        } else if (state.phase === 'challenge') {
            startCountdown();
            startPolling(true);
        }

        refreshCorridor();
        initIcons(elements.panel);
    }

    /** Show the panel on the phone step, restoring a held challenge. */
    function start() {
        show('phone');
    }

    /**
     * Show the panel on the code step, where the paid send already went out.
     * A user whose code never arrives reaches the free route without going back.
     */
    function startOnCodeStep() {
        show('code');
    }

    /**
     * Follow the page back to a step without reopening the panel.
     *
     * Several paths put the phone form back on screen without going through
     * {@see start}. The step decides the card's position and whether a
     * reverse-only corridor withdraws the paid send, so a stale one misplaces
     * both.
     */
    function setStep(step) {
        if (state.step === step) return;

        state.step = step;
        renderCorridor();
    }

    if (elements.showButton) {
        elements.showButton.addEventListener('click', function () {
            create();
        });
    }

    if (elements.startAgainButton) {
        elements.startAgainButton.addEventListener('click', function () {
            state.phase = 'idle';
            create();
        });
    }

    if (elements.copyButton) {
        elements.copyButton.addEventListener('click', function () {
            copyToken();
        });
    }

    [REVERSE_WHATSAPP, REVERSE_TELEGRAM].forEach(function (channel) {
        const link = elements.links && elements.links[channel];
        // The navigation is the anchor's own, so the handler only records that
        // the user left for the chat app.
        if (link) link.addEventListener('click', linkOpened);
    });

    visibility.subscribe(function () {
        if (visibility.isHidden()) {
            stopPolling();
            return;
        }

        if (state.phase !== 'challenge') return;
        if (remainingSeconds() <= 0) {
            expire();
            return;
        }

        startPolling(true);
    });

    return {
        /** Whether a create is in flight, for the page's own send guard. */
        busy: () => state.creating,
        clearRefusal,
        dropChallenge,
        hide,
        refreshCorridor,
        setStep,
        start,
        startOnCodeStep,
        state,
        stop,
    };
}
